import { CorrectOpType, ICorrectResult } from "@/common/entity/CorrectResult";
import { appColors } from "./AppConst";
import { logError, logToConsole } from "./LogHelper";
import { escapeRegExp, replaceRegex } from "./StringHelper";

const isLog = false;

/**
 * 把错误信息在文本中标记出来，并返回
 * @param htmlLine 需要标记文本所在的段落
 * @param correction 错误信息
 * @param id 标记的id
 * @param isHeightLight 是否高亮显示
 */
export function markCorrection(
    htmlLine: string,
    correction: ICorrectResult,
    id: string | number,
    isHeightLight: boolean = false
): string {
    const targetText = escapeRegExp(correction.text);
    if (isLog) logToConsole("markCorrection target=", correction);
    //通过不在<和>之间的错误文本分割文本组成数组
    const splitArr = htmlLine.split(new RegExp(`(${targetText}(?![^<]*>))`));
    if (isLog) logToConsole("markCorrection splitArr=", splitArr);
    //替换第correction.numberTh个错误文本
    if (!correction.numberTh) {
        logError("这里的numberTh不应该为空");
        return htmlLine;
    }
    const targetIndex = correction.numberTh * 2 - 1;
    if (!splitArr[targetIndex]) {
        logError("这里对应要替换的文本不应该为空");
        return htmlLine;
    }
    splitArr[targetIndex] = getMarkStr(id, correction, isHeightLight);
    return splitArr.join("");

    //小程序发布到体验版时无法使用（真机调试时可使用），貌似是因为小程序运行环境不支持Look behind正则表达式
    //网页端怕部分浏览器不支持Look behind正则表达式不使用这个了
    // return htmlLine.replace(
    //     replaceRegex(
    //         correction.text,
    //         correction.numberTh ? correction.numberTh - 1 : 0
    //     ),
    //     getMarkStr(id, correction, false)
    // );
}

/**
 * 获取对应correction的标记文本
 * @param id 标记的id
 * @param correction 错误信息
 * @param isHeightL 是否高亮
 */
function getMarkStr(
    id: string | number,
    correction: ICorrectResult,
    isHeightL: boolean
): string {
    const bgColor = isHeightL ? appColors.warningColor : undefined;
    return (
        correctionMark(id) +
        correctionStyle(
            correction.text,
            correction.op === CorrectOpType.delete,
            bgColor
        ) +
        correctionMark(id)
    );
}

/**
 *  高亮指定标记的文本
 * @param htmlLine 需要标记文本所在的段落
 * @param correction 错误信息
 * @param id 标记的id
 * @returns 返回高亮后的htmlLine
 */
export function highLight(
    htmlLine: string,
    correction: ICorrectResult,
    id: string | number
): string {
    const markStr = getMarkStr(id, correction, true);
    if (isLog) logToConsole("markStr=", markStr);
    return htmlLine.replace(getRegexMatchId(id), markStr);
}

/**
 * 取消高亮指定标记的文本
 * @param htmlLine 需要标记文本所在的段落
 * @param correction 错误信息
 * @param id 标记的id
 * @returns 返回取消高亮后的htmlLine
 */
export function cancelHighLight(
    htmlLine: string,
    correction: ICorrectResult,
    id: string | number
): string {
    return htmlLine.replace(
        getRegexMatchId(id),
        getMarkStr(id, correction, false)
    );
}

/**
 * 进行纠错操作
 * @param htmlLine 需要标记文本所在的段落
 * @param correction 错误信息
 * @param id 标记的id
 */
export function doCorrection(
    htmlLine: string,
    correction: ICorrectResult,
    id: string | number
): string {
    if (correction.op == CorrectOpType.noAction) return htmlLine;
    let newStr = "";
    switch (correction.op) {
        case CorrectOpType.delete:
            newStr = "";
            break;
        case CorrectOpType.preAdd:
            newStr = correction.repl_str + correction.text;
            break;
        case CorrectOpType.sufAdd:
            newStr = correction.text + correction.repl_str;
            break;
        case CorrectOpType.replace:
            if (correction.repl_str == correction.text)
                newStr = correction.repl_str;
            else
                newStr = correction.text.replace(
                    correction.old_str,
                    correction.repl_str
                );
            break;
    }
    return htmlLine.replace(getRegexMatchId(id), newStr);
}

/**
 * 获取匹配对应id的文本的正则表达式
 * @param id 标记的id
 */
function getRegexMatchId(id: string | number): RegExp {
    const mark = escapeRegExp(correctionMark(id));
    return new RegExp(`${mark}.*${mark}`);
}

/**
 * 去除htmlContent中的所有纠错标记
 * @param htmlContent 取出此值中所有的标记
 * @returns 返回去掉后的结果
 */
export function removeAllMark(htmlContent?: string): string {
    if (isLog)
        logToConsole("removeAllMark before remove htmlContent=", htmlContent);
    if (!htmlContent) return "";
    return htmlContent.replace(
        /(<correction-(.*)><\/correction-\2>)<span [^>]*>(?:<del>(.*)<\/del>|(.*))<\/span>\1/g,
        "$3$4"
    );
}

/**
 * 获取错误消息前后需要添加的标记
 * @param id 标记的id
 */
export function correctionMark(id: string | number): string {
    return `<correction-${id}></correction-${id}>`;
}

/**
 * 使用html方式为text添加样式
 * @param text 目标文本
 * @param isDelete 是否标为删除状态
 * @param bgColor 背景颜色
 * @param colorStr 文本颜色
 * @returns 添加样式后的文本
 */
function correctionStyle(
    text: string,
    isDelete: boolean = false,
    bgColor: string | null = null,
    colorStr = appColors.correctTextColor()
): string {
    const style =
        `color:${colorStr};` + (bgColor ? `background-color:${bgColor};` : "");
    const delTag = isDelete ? "<del>" : "";
    const delEndTag = isDelete ? "</del>" : "";
    return `<span style="${style}">${delTag}${text}${delEndTag}</span>`;
}
